/* ── WORK_TRACK — Time entries ────────────────────────────── */

import {
  $,
  esc,
  icon,
  api,
  state,
  toast,
  registerView,
  openModal,
  closeModal,
  setFieldError,
  clearFieldErrors,
  projectOptions,
  taskOptions,
  todayStr,
  loadingHtml,
  emptyHtml,
  fmtMin
} from './core';
import { loadDashboard } from './dashboard';
import { loadTodayLog } from './timer';
import type { TimeEntry } from './types';

export async function loadEntries(): Promise<void> {
  const ul = $('#entriesList');
  const date = $<HTMLInputElement>('#entriesDate').value || '';
  ul.innerHTML = loadingHtml();
  try {
    const url = date ? `/api/entries?date=${date}` : '/api/entries';
    const entries = await api<TimeEntry[]>(url);
    if (!entries.length) {
      ul.innerHTML = emptyHtml(
        icon('timer', 30),
        date ? 'No entries for this date' : 'No entries yet',
        date ? 'Try another day or log some time.' : 'Log your first session to start tracking.'
      );
      return;
    }
    ul.innerHTML = entries
      .map(
        e => `
      <li data-entry="${e.id}">
        <div class="item-main">
          <div class="item-title">${esc(e.project_name || 'General')}${e.task_title ? ' · ' + esc(e.task_title) : ''}</div>
          <div class="item-sub">${esc(e.entry_date)}${e.note ? ' — ' + esc(e.note) : ''}</div>
        </div>
        <div class="item-right">
          <span class="item-val">${fmtMin(e.minutes)}</span>
          <div class="tc-actions">
            <button class="tc-link" data-eaction="edit" data-entry="${e.id}">Edit</button>
            <button class="tc-link" data-eaction="delete" data-entry="${e.id}">Delete</button>
          </div>
        </div>
      </li>`
      )
      .join('');
  } catch {
    ul.innerHTML = '<li class="empty">Could not load.</li>';
  }
}

$<HTMLInputElement>('#entriesDate').addEventListener('change', loadEntries);

document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  const edit = target.closest('[data-eaction="edit"]');
  if (edit) {
    openEntryModal(Number(edit.getAttribute('data-entry')), loadEntries);
    return;
  }
  const del = target.closest('[data-eaction="delete"]');
  if (del) {
    deleteEntry(Number(del.getAttribute('data-entry')));
    return;
  }
});

async function deleteEntry(id: number): Promise<void> {
  if (!confirm('Delete this time entry?')) return;
  try {
    await api(`/api/entries/${id}`, 'DELETE');
    toast('Entry deleted');
    loadEntries();
    loadTodayLog();
    if (state.view === 'dashboard') loadDashboard();
  } catch (e) {
    toast((e as Error).message, true);
  }
}

$('#newEntryBtn').addEventListener('click', () => openEntryModal(null, loadEntries));

function openEntryModal(id: number | null, onDone: (() => void) | null): void {
  (async () => {
    let entry: TimeEntry | null = null;
    if (id) {
      try {
        entry = (await api<TimeEntry[]>('/api/entries')).find(x => x.id === id) || null;
      } catch {
        /* fall through with entry = null */
      }
    }
    const card = openModal(`
      <form class="modal-form" id="entryForm">
        <div class="modal-head"><h2>${entry ? 'Edit entry' : 'Log time'}</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
        <div class="field"><label>Date</label><input type="date" name="entry_date" value="${esc(entry ? entry.entry_date : todayStr())}"></div>
        <div class="row2">
          <div class="field"><label>Project</label>
            <select name="project_id">${entry && entry.project_id ? projectOptions(entry.project_id) : `<option value="">No project</option>` + projectOptions()}</select>
          </div>
          <div class="field"><label>Task</label>
            <select name="task_id"><option value="">(no task)</option>${entry && entry.task_id ? taskOptions(entry.project_id, entry.task_id) : taskOptions()}</select>
          </div>
        </div>
        <div class="field"><label>Minutes</label><input type="number" name="minutes" min="1" step="1" required value="${entry ? entry.minutes : 30}"></div>
        <div class="field"><label>Note</label><input type="text" name="note" maxlength="500" value="${esc(entry ? entry.note || '' : '')}"></div>
        <div class="modal-actions">
          <button type="button" class="btn btn-ghost" id="modalCancel">Cancel</button>
          <button type="submit" class="btn btn-primary">${entry ? 'Save' : 'Log'}</button>
        </div>
      </form>`);
    card.querySelector('#modalCancel')?.addEventListener('click', closeModal);
    const projectSelect = card.querySelector<HTMLSelectElement>('select[name="project_id"]')!;
    projectSelect.addEventListener('change', () => {
      const proj = Number(projectSelect.value);
      card.querySelector<HTMLSelectElement>('select[name="task_id"]')!.innerHTML =
        '<option value="">(no task)</option>' +
        state.tasks
          .filter(t => !proj || t.project_id === proj)
          .map(t => `<option value="${t.id}">${esc(t.title)}</option>`)
          .join('');
    });
    card.querySelector<HTMLFormElement>('#entryForm')!.addEventListener('submit', async e => {
      e.preventDefault();
      clearFieldErrors(card);
      const f = new FormData(card.querySelector<HTMLFormElement>('#entryForm')!);
      const minutes = Number(f.get('minutes'));
      if (!Number.isFinite(minutes) || minutes <= 0) {
        const inp = card.querySelector<HTMLInputElement>('input[name="minutes"]')!;
        setFieldError(inp, 'Minutes must be a positive number');
        inp.focus();
        return;
      }
      const submitBtn = card.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('loading');
        submitBtn.innerHTML = `<span class="spinner"></span>${esc(entry ? 'Saving…' : 'Logging…')}`;
      }
      const payload = {
        entry_date: String(f.get('entry_date') || todayStr()),
        project_id: f.get('project_id') ? Number(f.get('project_id')) : null,
        task_id: f.get('task_id') ? Number(f.get('task_id')) : null,
        minutes,
        note: String(f.get('note') || '')
      };
      try {
        if (entry) {
          await api(`/api/entries/${entry.id}`, 'PUT', payload);
          toast('Entry updated');
        } else {
          await api('/api/entries', 'POST', payload);
          toast('Time logged');
        }
        closeModal();
        if (onDone) onDone();
        loadTodayLog();
        if (state.view === 'dashboard') loadDashboard();
      } catch (err) {
        toast((err as Error).message, true);
      } finally {
        const sb = card.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (sb) {
          sb.disabled = false;
          sb.classList.remove('loading');
          sb.innerHTML = esc(entry ? 'Save' : 'Log');
        }
      }
    });
  })();
}

registerView('entries', loadEntries);
